interface BoundaryDiagramProps {
  /** 0 → 1, ties the entrance motion to the section's scroll-reveal progress */
  progress: number;
  className?: string;
}

// Points that fall inside the BrandPick circle but outside the existing-
// customer circle (verified against the two circles' geometry below), each
// with a stagger so they read as customers being found one after another
// rather than all appearing at once. Positioned on a rough arc sweeping
// around the crescent (top → right → bottom) at a consistent ~10-20px
// margin from both circle edges, with spacing kept above ~40px between
// any two dots — evenly spread rather than clustered/gappy.
const DOTS = [
  { cx: 188, cy: 28, delay: 0.3 },
  { cx: 245, cy: 35, delay: 0.9 },
  { cx: 292, cy: 80, delay: 0.5 },
  { cx: 280, cy: 130, delay: 1.3 },
  { cx: 225, cy: 165, delay: 0.7 },
  { cx: 172, cy: 150, delay: 1.6 },
  { cx: 255, cy: 100, delay: 1.0 },
];

/**
 * Incremental-reach Venn diagram: one circle stands for the brand's existing
 * customers, a larger overlapping circle stands for everyone BrandPick can
 * target. Only the crescent — BrandPick's circle minus the overlap with the
 * existing-customer circle — is highlighted, since that's the exclusively
 * new audience BrandPick actually delivers to (it explicitly excludes people
 * already in the brand's own channel, matching the copy elsewhere on the
 * page). The overlap region intentionally stays neutral.
 */
export default function BoundaryDiagram({ progress, className }: BoundaryDiagramProps) {
  // Reveal fully well before the scroll range ends so it doesn't sit
  // half-transparent through most of the scroll — thin strokes and small
  // dots read as faint noise at partial opacity.
  const reveal = Math.min(1, progress / 0.7);

  return (
    <div
      className={`tw-relative tw-mx-auto tw-w-full tw-max-w-[280px] md:tw-max-w-[400px] ${className ?? ""}`}
      style={{
        opacity: reveal,
        transform: `scale(${0.9 + reveal * 0.1})`,
        willChange: "opacity, transform",
      }}
      aria-hidden="true"
    >
      <div className="tw-relative tw-aspect-[3/2] tw-w-full">
        <svg viewBox="0 0 300 200" className="tw-absolute tw-inset-0 tw-h-full tw-w-full tw-overflow-visible">
          <defs>
            <radialGradient id="brandpickCrescentGlow" cx="58%" cy="50%" r="75%">
              <stop offset="0%" stopColor="#FEE500" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#FEE500" stopOpacity="0.05" />
            </radialGradient>
            <mask id="brandpickCrescentMask">
              {/* Sized past circle B's actual extent (cx 205, r 100 → reaches
                  x 305) rather than matching the viewBox — a mask's own rect
                  clips its content independently of the viewBox, so a rect
                  stopping at x=300 was slicing a flat edge out of the circle
                  instead of the circle's own curve. */}
              <rect x="-50" y="-50" width="400" height="300" fill="white" />
              <circle cx="100" cy="100" r="70" fill="black" />
            </mask>
          </defs>

          {/* existing customers */}
          <circle
            cx="100"
            cy="100"
            r="70"
            fill="white"
            fillOpacity="0.06"
            stroke="white"
            strokeOpacity="0.38"
            strokeWidth="1.5"
          />

          {/* full brandpick boundary, drawn under the crescent fill */}
          <circle
            cx="205"
            cy="100"
            r="100"
            fill="none"
            stroke="#FEE500"
            strokeOpacity="0.5"
            strokeWidth="1.5"
          />

          {/* flat base tint so the crescent reads as evenly colored all the
              way to its outer edge, not just where the glow below is bright */}
          <circle
            cx="205"
            cy="100"
            r="100"
            fill="#FEE500"
            fillOpacity="0.16"
            mask="url(#brandpickCrescentMask)"
          />

          {/* only the part of the brandpick circle outside existing customers */}
          <circle
            cx="205"
            cy="100"
            r="100"
            fill="url(#brandpickCrescentGlow)"
            mask="url(#brandpickCrescentMask)"
            style={{
              animation: "crescent-breathe 4s ease-in-out infinite",
              transformOrigin: "205px 100px",
            }}
          />

          {DOTS.map((d, i) => (
            <circle
              key={i}
              cx={d.cx}
              cy={d.cy}
              r="3.2"
              fill="#FEE500"
              style={{
                filter: "drop-shadow(0 0 4px rgba(254,229,0,0.85))",
                transformBox: "fill-box",
                transformOrigin: "center",
                animation: `dot-pop 600ms cubic-bezier(0.34, 1.56, 0.64, 1) ${d.delay}s backwards`,
              }}
            />
          ))}
        </svg>

        <div className="tw-absolute tw-left-[19%] tw-top-1/2 tw-max-w-[30%] tw--translate-x-1/2 tw--translate-y-1/2 tw-text-center">
          <span className="tw-text-[10px] tw-font-semibold tw-leading-tight tw-text-white md:tw-text-xs">
            보유
            <br />
            고객
          </span>
        </div>

        <div className="tw-absolute tw-left-[82%] tw-top-[26%] tw-max-w-[54%] tw--translate-x-1/2 tw--translate-y-1/2 tw-whitespace-nowrap tw-rounded-full tw-bg-[#FEE500] tw-px-2.5 tw-py-1 tw-text-center tw-text-[9px] tw-font-semibold tw-leading-tight tw-text-[#202020] tw-shadow-[0_2px_10px_rgba(0,0,0,0.35)] md:tw-px-3 md:tw-py-1.5 md:tw-text-[11px]">
          브랜드픽 · 새 잠재고객
        </div>
      </div>
    </div>
  );
}
